import { atom, selector } from 'recoil'
import Decimal from 'decimal.js'
import {
  marketMapState,
  markPriceMapState,
  futuresMarketInfoMapState
} from '@/recoil/Market'
import { accountInfoState } from '@/recoil/AccountInfo'
import { accountWalletState } from '@/recoil/Wallet'
import { USDC_TOKEN_LIST } from '@/constants/token'
import {
  calculatePositionNotionalValue,
  calculateInitialMarginRatio,
  calculateMaintenanceMarginRatio,
  calculateMaintenanceMargin,
  calculateUnrealPnl,
  calculateUnSettledPnl,
  calculateLiquidationPrice
} from '@/utils/futures'

export const showSettlePnlPopupState = atom({
  key: 'ShowSettlePnlPopup',
  default: false
})

export const closePositionPopupState = atom({
  key: 'ClosePositionPopup',
  default: {
    show: false,
    marketSymbol: ''
  }
})

export const positionInfoState = atom({
  key: 'PositionInfo',
  default: {}
})

export const positionMapValue = selector({
  key: 'PositionMap',
  get: ({ get }) => {
    const { rows: positionList = [] } = get(positionInfoState)
    const map = {}
    positionList.forEach(position => {
      map[position.symbol] = position
    })
    return map
  },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent'
  }
})

export const positionDetailValue = selector({
  key: 'PositionDetail',
  get: ({ get }) => {
    const marketMap = get(marketMapState)
    const markPriceMap = get(markPriceMapState)
    const futuresMarketInfoMap = get(futuresMarketInfoMapState)
    const { rows: positionList = [] } = get(positionInfoState)
    const { max_leverage: accountMaxLeverage, imr_factor: imrFactorMap = {} } =
      get(accountInfoState)
    const initialMarginMap = {}
    const maintenanceMarginMap = {}
    const maintenanceMarginRatioMap = {}
    // const unrealPnlMap = {}
    // const unSettledPnlMap = {}

    let totalPositionNotionalValue = 0
    let totalInitialMargin = 0
    let totalMaintenanceMargin = 0
    let totalDailyPnl = 0
    let totalUnrealPnl = 0
    let totalUnsettledPnl = 0

    positionList.forEach(
      ({
        symbol,
        position_qty: positionQty,
        pending_long_qty = 0,
        pending_short_qty = 0,
        average_open_price,
        cost_position,
        last_sum_unitary_funding,
        pnl_24_h
      }) => {
        const marketInfo = marketMap[symbol]
        const markPrice = markPriceMap[symbol]
        const futuresMarketInfo = futuresMarketInfoMap[symbol]
        const imrFactor = imrFactorMap[symbol]

        if (
          !accountMaxLeverage ||
          !marketInfo ||
          !markPrice ||
          !futuresMarketInfo ||
          !imrFactor ||
          positionQty === undefined
        ) {
          return
        }

        const { base_imr: baseImr, base_mmr: baseMmr } = marketInfo

        const positionNotionalValue = calculatePositionNotionalValue(
          positionQty,
          markPrice
        )
        const netPositionNotionalValue = Decimal.max(
          Decimal(pending_long_qty).add(positionQty).abs(),
          Decimal(pending_short_qty).abs().sub(positionQty).abs()
        )
          .times(markPrice)
          .toNumber()
        const initialMarginRatio = calculateInitialMarginRatio({
          accountMaxLeverage,
          baseImr,
          imrFactor,
          netPositionNotionalValue
        })
        const initialMargin = Decimal(netPositionNotionalValue)
          .mul(initialMarginRatio)
          .toNumber()
        const maintenanceMarginRatio = calculateMaintenanceMarginRatio({
          baseMmr,
          baseImr,
          imrFactor,
          positionNotionalValue
        })
        const maintenanceMargin = calculateMaintenanceMargin(
          positionNotionalValue,
          maintenanceMarginRatio
        )
        const unrealPnl = calculateUnrealPnl(
          positionQty,
          markPrice,
          average_open_price
        )
        const unSettledPnl = calculateUnSettledPnl({
          positionQty,
          markPrice,
          sumUnitaryFunding: futuresMarketInfo.sum_unitary_funding,
          lastSumUnitaryFunding: last_sum_unitary_funding,
          costPosition: cost_position
        })

        // unrealPnlMap[symbol] = unrealPnl
        // unSettledPnlMap[symbol] = unSettledPnl
        initialMarginMap[symbol] = initialMargin
        maintenanceMarginMap[symbol] = maintenanceMargin
        maintenanceMarginRatioMap[symbol] = maintenanceMarginRatio

        totalPositionNotionalValue = Decimal(totalPositionNotionalValue)
          .add(positionNotionalValue)
          .toNumber()
        totalInitialMargin = Decimal(totalInitialMargin)
          .add(initialMargin)
          .toNumber()
        totalMaintenanceMargin = Decimal(totalMaintenanceMargin)
          .add(maintenanceMargin)
          .toNumber()
        totalDailyPnl = Decimal(totalDailyPnl).add(pnl_24_h).toNumber()
        totalUnrealPnl = Decimal(totalUnrealPnl).add(unrealPnl).toNumber()
        totalUnsettledPnl = Decimal(totalUnsettledPnl)
          .add(unSettledPnl)
          .toNumber()
      }
    )

    return {
      // unrealPnlMap,
      // unSettledPnlMap,
      initialMarginMap,
      maintenanceMarginMap,
      maintenanceMarginRatioMap,

      totalPositionNotionalValue,
      totalInitialMargin,
      totalMaintenanceMargin,
      totalDailyPnl,
      totalUnrealPnl,
      totalUnsettledPnl
    }
  },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent'
  }
})

export const collateralInfoValue = selector({
  key: 'CollateralInfo',
  get: ({ get }) => {
    const accountWalletMap = get(accountWalletState)
    const markPriceMap = get(markPriceMapState)
    let totalCollateralValue = 0
    let availableCollateralValue = 0
    Object.keys(accountWalletMap).forEach(symbol => {
      const isUsdc = USDC_TOKEN_LIST.includes(symbol)
      const markPrice = isUsdc ? 1 : markPriceMap[`SPOT_${symbol}_USDC.e`]
      const accountWallet = accountWalletMap[symbol]
      const collateralRate = isUsdc ? 1 : 0

      if (!markPrice || !accountWallet || !collateralRate) {
        return
      }
      const collateralPriceRatio = Decimal(markPrice).mul(collateralRate)
      const totalValue = Decimal(accountWallet.holding)
        .add(accountWallet.interest)
        .sub(Decimal(accountWallet.pendingShortQty).abs())
        .mul(collateralPriceRatio)
        .toNumber()
      const availableValue = Decimal(accountWallet.available)
        .mul(collateralPriceRatio)
        .toNumber()
      totalCollateralValue = Decimal(totalCollateralValue)
        .add(totalValue)
        .toNumber()
      availableCollateralValue = Decimal(availableCollateralValue)
        .add(availableValue)
        .toNumber()
    })

    const { totalUnsettledPnl, totalInitialMargin } = get(positionDetailValue)

    return {
      total: totalCollateralValue,
      available: availableCollateralValue,
      totalCollateral: Decimal(totalCollateralValue)
        .add(totalUnsettledPnl)
        .toNumber(),
      freeCollateral: Decimal(availableCollateralValue)
        .add(totalUnsettledPnl)
        .sub(totalInitialMargin)
        .toNumber()
    }
  },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent'
  }
})

export const liquidationPriceMapValue = selector({
  key: 'LiquidationPriceMap',
  get: ({ get }) => {
    const map = {}
    const markPriceMap = get(markPriceMapState)
    const { rows: positionList = [] } = get(positionInfoState)
    const { maintenanceMarginRatioMap, totalMaintenanceMargin } =
      get(positionDetailValue)
    const { totalCollateral } = get(collateralInfoValue)

    positionList.forEach(({ symbol, position_qty }) => {
      const maintenanceMarginRatio = maintenanceMarginRatioMap[symbol]
      const markPrice = markPriceMap[symbol]
      map[symbol] = calculateLiquidationPrice({
        positionQty: position_qty,
        markPrice,
        totalCollateral,
        maintenanceMarginRatio,
        totalMaintenanceMargin
      })
    })
    return map
  },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent'
  }
})
